/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-unused-vars */
import CloseIcon from '@mui/icons-material/Close'
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar'
import FlightLandIcon from '@mui/icons-material/FlightLand'
import GroupIcon from '@mui/icons-material/Group'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Typography,
} from '@mui/material'
import { JourneyType } from 'app/constants/Booking/BookingConstants'
import DirectionsMap from 'components/Common/DirectionsMap/DirectionsMap'
import DisplayPrice from 'components/Common/DisplayValues/DisplayPrice'
import { useWizard } from 'components/RouteWizard/Wizard'
import useBooking from 'hooks/useBooking'
import useClients from 'hooks/useClients'
import useFlightLookup from 'hooks/useFlightLookup'
import useOnwardBookings, {
  OnwardQuoteSuggestion,
} from 'hooks/useOnwardBookings'
import moment from 'moment'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { setCancelSuccess, setQuickQuote } from 'reducers/bookingsReducer'
import { looksLikeAirport } from '../../../Pages/BookingsPage/CreateBookingPage/components/AirportServices'
import OnwardBookingCards from '../../../Pages/BookingsPage/CreateBookingPage/components/OnwardBookingCards'
import IconHandshake from '../../../assets/icon/icon-handshake.svg'
import IconInfo from './IconInfo'
import SummaryItem from './SummaryItem'

const Confirmation = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const { data: summaryData, addData, clearData, clearNoRedirect } = useWizard()
  const { cancelVehicle, error, isLoading } = useBooking()
  const [handleData, setHandleData] = React.useState<any>()
  const [cancelTrigger, setCancelTrigger] = React.useState<boolean>(false)

  const preview = {
    ...handleData?.preview,
  }

  const getPlanBound = handleData?.plan
    ? Object.entries(handleData?.plan)
        .filter((v) => {
          const getTransportReference = handleData?.transport?.[v[0]]
          const details =
            (v[0] === 'inbound' || v[0] === 'outbound') && getTransportReference
          return details
        })
        .map((v: any) => {
          const getTransportReference = handleData?.transport?.[v[0]]
          return {
            ...v[1],
            isImmediateJourney: handleData?.plan?.isImmediateJourney,
            routeType: v[0],
            transportRef: getTransportReference,
          }
        })
    : []

  React.useEffect(() => {
    if (!error && !isLoading && cancelTrigger) {
      dispatch(setCancelSuccess(true))
      history.replace('/admin/bookings/create')
    }
  }, [error, isLoading, cancelTrigger])

  React.useEffect(() => {
    dispatch(setQuickQuote(false))
    setHandleData(summaryData)
    // NOTE: previously this also called clearNoRedirect() which wiped the
    // wizard's session-storage snapshot. That caused the booking data to
    // disappear on any remount of this component (HMR, hard refresh, nav
    // back/forward) and broke the onward-journey flow (`handleData` ended up
    // empty at click time, so account/preview data couldn't be carried
    // forward). The "New Booking" button still calls clearData() explicitly
    // when the user wants a fresh wizard.
    return () => {
      setHandleData(undefined)
    }
  }, [])

  const passengerCount =
    handleData?.account?.numPassengers ||
    handleData?.account?.numberOfPassengers

  // Resolve the account number — the legacy wizard stores clientId but not
  // accountNumber. Look it up from the clients list (falling back to the
  // logged-in client for the "my account" flow).
  const currentClient = useSelector((state: any) => state.clients?.client)
  const { clients: allClients } = useClients()
  const resolvedAccountNumber =
    allClients?.find((c: any) => c?.id === handleData?.account?.clientId)
      ?.accountNumber ??
    (currentClient?.id === handleData?.account?.clientId
      ? currentClient?.accountNumber
      : undefined)

  // ========================================================================
  // ONWARD JOURNEY SUGGESTIONS
  // Mirrors the behaviour on the new CreateBookingPage confirmation step:
  // when the user has booked a journey that ends at an airport with a flight
  // number attached, offer pre-quoted onward journeys from that airport to
  // likely destinations.
  // ========================================================================
  const onwardPickup = handleData?.plan?.outbound?.pickup
  const onwardDropoff = handleData?.plan?.outbound?.dropOff
  const onwardWhen = handleData?.plan?.outbound?.when
  const pickupIsAirport = looksLikeAirport(onwardPickup)
  const dropoffIsAirport = looksLikeAirport(onwardDropoff)
  const legacyJourneyTypeName = handleData?.plan?.journeyTypeName
  const isReturnJourney =
    legacyJourneyTypeName === 'Return' ||
    legacyJourneyTypeName === 'WaitAndReturn'

  const outboundFlightNumber =
    handleData?.plan?.outbound?.flightDetails?.flightNumber
  const inboundFlightNumber =
    handleData?.plan?.inbound?.flightDetails?.flightNumber

  const shouldOfferOnward =
    (dropoffIsAirport && !!outboundFlightNumber?.trim()) ||
    (pickupIsAirport && isReturnJourney && !!inboundFlightNumber?.trim())

  const onwardFlightNumber = dropoffIsAirport
    ? outboundFlightNumber
    : inboundFlightNumber

  const toLocalDateStr = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate(),
    ).padStart(2, '0')}`
  const onwardDateStr = onwardWhen
    ? toLocalDateStr(new Date(onwardWhen))
    : undefined

  const { flight: onwardFlight } = useFlightLookup({
    flightNumber: onwardFlightNumber,
    date: onwardDateStr,
    isArrival: false,
    enabled:
      shouldOfferOnward && !!onwardFlightNumber?.trim() && !!onwardDateStr,
  })

  // NOTE: `useOnwardBookings` gates quote-fetching on `currentStep === 'vehicle'`
  // in the new wizard. On the legacy confirmation we're already past that
  // point, so pass 'vehicle' to force the quote fetch once destinations are
  // available. This is the single-call-site workaround for legacy; don't
  // generalise until there's a second caller.
  const onwardBookings = useOnwardBookings({
    clientId: handleData?.account?.clientId,
    leadPassengerId: handleData?.account?.mainPassenger?.userId,
    flightNumber: onwardFlightNumber,
    pickupDateTime: onwardWhen ? new Date(onwardWhen).toISOString() : undefined,
    arrivalAirport: onwardFlight?.arrival ?? undefined,
    accountNumber: handleData?.account?.accountNumber ?? resolvedAccountNumber,
    numberOfPassengers: passengerCount,
    passengers: handleData?.preview?.passengers,
    mainPassenger: handleData?.account?.mainPassenger,
    currentStep: 'vehicle',
  })

  React.useEffect(() => {
    console.log('[Onward:legacy] handleData set:', Boolean(handleData))
    console.log('[Onward:legacy] pickup:', onwardPickup)
    console.log('[Onward:legacy] dropoff:', onwardDropoff)
    console.log('[Onward:legacy] pickupIsAirport:', pickupIsAirport)
    console.log('[Onward:legacy] dropoffIsAirport:', dropoffIsAirport)
    console.log('[Onward:legacy] outboundFlightNumber:', outboundFlightNumber)
    console.log('[Onward:legacy] inboundFlightNumber:', inboundFlightNumber)
    console.log('[Onward:legacy] shouldOfferOnward:', shouldOfferOnward)
    console.log('[Onward:legacy] onwardFlightNumber:', onwardFlightNumber)
    console.log('[Onward:legacy] onwardDateStr:', onwardDateStr)
    console.log('[Onward:legacy] onwardFlight:', onwardFlight)
    console.log('[Onward:legacy] clientId:', handleData?.account?.clientId)
    console.log(
      '[Onward:legacy] accountNumber (stored):',
      handleData?.account?.accountNumber,
    )
    console.log(
      '[Onward:legacy] accountNumber (resolved):',
      resolvedAccountNumber,
    )
    console.log('[Onward:legacy] suggestions:', onwardBookings.suggestions)
    console.log(
      '[Onward:legacy] isLoadingLocations:',
      onwardBookings.isLoadingLocations,
    )
    console.log(
      '[Onward:legacy] isLoadingQuotes:',
      onwardBookings.isLoadingQuotes,
    )
  }, [
    handleData,
    onwardFlight,
    onwardBookings.suggestions,
    onwardBookings.isLoadingLocations,
    onwardBookings.isLoadingQuotes,
  ])

  // Modal open/dismiss state - auto-opens once suggestions arrive, dismissable
  // via close button / backdrop / ESC.
  const [onwardModalDismissed, setOnwardModalDismissed] =
    React.useState<boolean>(false)
  const onwardModalOpen =
    onwardBookings.suggestions.length > 0 && !onwardModalDismissed

  const handleOnwardSelect = (suggestion: OnwardQuoteSuggestion) => {
    const account = handleData?.account
    const type = handleData?.type
    const onwardPlan = {
      journeyTypeName: JourneyType.ONE_WAY,
      outbound: {
        pickup: {
          addressLine1: suggestion.arrivalAirport.name,
          formattedAddress: suggestion.arrivalAirport.name,
          latitude: suggestion.arrivalAirport.latitude,
          longitude: suggestion.arrivalAirport.longitude,
          countryCode: suggestion.arrivalAirport.countryCode,
        },
        dropOff: {
          addressLine1: suggestion.destination.addressLine1,
          addressLine2: suggestion.destination.addressLine2,
          formattedAddress: [
            suggestion.destination.addressLine1,
            suggestion.destination.town,
            suggestion.destination.postCode,
          ]
            .filter(Boolean)
            .join(', '),
          town: suggestion.destination.town,
          postCode: suggestion.destination.postCode,
          country: suggestion.destination.country,
          countryCode: suggestion.destination.countryCode,
          latitude: suggestion.destination.latitude,
          longitude: suggestion.destination.longitude,
        },
        when: new Date(suggestion.arrivalDateTime),
        flightDetails: { flightNumber: suggestion.flightNumber },
        services: '',
        specialInstructions: '',
      },
    }

    // addData merges with the pre-click `data` captured in the closure, so
    // pass explicit overrides for every top-level wizard key. Account +
    // passenger info is preserved; plan is swapped; transport/prices are
    // wiped so the new journey re-quotes cleanly. `preview` is kept because
    // JourneyPlan's BookingSummary reads `preview.companyName` and
    // `preview.passengers` from it - clearing it leaves the Plan step with
    // no client/passenger context.
    const nextWizardData = {
      type,
      account,
      plan: onwardPlan,
      preview: {
        companyName: handleData?.preview?.companyName,
        passengers: handleData?.preview?.passengers,
        fixedPrice: handleData?.preview?.fixedPrice,
        displayVATPrice: handleData?.preview?.displayVATPrice,
      },
      transport: undefined,
      prices: undefined,
    }
    addData(nextWizardData)

    console.log('[Onward:legacy] handleData.preview:', handleData?.preview)
    console.log('[Onward:legacy] nextWizardData:', nextWizardData)
    console.log('[Onward:legacy] account:', account)
    console.log(
      '[Onward:legacy] preview.companyName:',
      nextWizardData.preview?.companyName,
    )
    console.log(
      '[Onward:legacy] preview.passengers:',
      nextWizardData.preview?.passengers,
    )
    history.push('/admin/bookings/create/plan')
  }

  return (
    <Box
      component="div"
      sx={{
        width: '100%',
      }}
    >
      <DirectionsMap
        origin={preview?.pickup}
        destination={preview?.dropOff}
        additionalStops={preview?.additionalStops}
        mapWidth="calc(100dvw / var(--desktop-zoom-factor, 1))"
        mapHeight="300px"
        marginTop="-20px"
        marginLeft="-21.43%"
        marginRight="-21.43%"
      />
      <Box
        component="div"
        sx={{
          backgroundColor: 'rgba(2, 29, 46, 0.5)',
          marginLeft: '-21.43%',
          marginRight: '-21.43%',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box
          component="div"
          sx={(theme) => ({
            backgroundColor: theme.palette.brand.main,
            gap: '10px',
            padding: '0 20px',
            display: 'grid',
            gridTemplateColumns: '150px 150px',
            placeItems: 'center',
            placeContent: 'center',
            '@media (min-width: 1200px)': {
              gap: '20px',
              padding: '0 60px',
              gridTemplateColumns: '150px 150px 150px 150px 200px',
            },
          })}
        >
          <SummaryItem title="booking type" value={handleData?.type} />
          <SummaryItem title="client" value={preview.companyName} />
          <SummaryItem
            title="est journey time"
            value={
              handleData?.plan?.journeyTypeName === JourneyType.AS_DIRECTED
                ? `${handleData?.plan?.directedJourneyTime} HOURS`
                : handleData?.transport?.outbound?.estimatedJourneyTime != null
                ? Math.round(
                    handleData.transport.outbound.estimatedJourneyTime / 60,
                  ) + ' mins'
                : ''
            }
          />
          <SummaryItem
            title="est distance"
            value={
              handleData?.plan?.journeyTypeName === JourneyType.AS_DIRECTED
                ? 'Hourly Trip'
                : handleData?.transport?.outbound?.estimatedJourneyDistance !=
                  null
                ? handleData.transport.outbound.estimatedJourneyDistance +
                  ' miles'
                : ''
            }
          />
          <SummaryItem
            title={'Price'}
            value={DisplayPrice({
              VATTotalPrice: preview.estimatedPriceFull,
              priceType: handleData?.account?.fixedPrice ? 'exact' : 'range',
              displayVATPrice: preview.displayVATPrice,
              fontWeight: 'bold',
            })}
          />
        </Box>
      </Box>
      <Box
        component="div"
        sx={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: '#414042',
          display: 'flex',
          flexGrow: 0,
          flexDirection: 'row',
          flexWrap: 'wrap',
          justifyContent: 'space-evenly',
          alignItems: 'center',
          gap: '10px',
          marginTop: '50px',
          marginBottom: '-30px',
        }}
      >
        BOOKING RECEIVED
      </Box>
      <Box
        component="div"
        sx={{
          display: 'flex',
          flexGrow: 0,
          flexDirection: 'row',
          flexWrap: 'wrap',
          justifyContent: 'space-evenly',
          alignItems: 'center',
          gap: '10px',
          marginTop: '50px',
          textAlign: 'center',
          marginBottom: '40px',
        }}
      >
        We are allocating your booking to our supply chain. <br />
        The passenger will receive an SMS confirmation once a driver has been
        allocated immediately prior to the pickup time.
      </Box>
      <Box
        component="div"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '48px',
          maxWidth: '1328px',
          margin: '0 auto',
        }}
      >
        {getPlanBound?.map((v, key) => {
          const vehicleCountMap = v.transportRef?.vehicles?.reduce(
            (acc: any, vehicle: any) => {
              acc[vehicle.vehicleTypeName] =
                (acc[vehicle.vehicleTypeName] || 0) + 1
              return acc
            },
            {},
          )
          const vehicleCountArray = vehicleCountMap
            ? Object.keys(vehicleCountMap).map((vehicleTypeName) => ({
                vehicleTypeName,
                count: vehicleCountMap[vehicleTypeName],
              }))
            : []
          const cardContent = [
            {
              icon: (
                <IconInfo
                  Icon={LocationOnIcon}
                  title="FROM"
                  value={v.pickup?.formattedAddress}
                />
              ),
            },
            {
              icon: (
                <IconInfo
                  Icon={LocationOnIcon}
                  title="TO"
                  value={v.dropOff?.formattedAddress}
                />
              ),
            },
            {
              label: 'VIA',
              value:
                v.pickup?.pointOfInterestTypeName ||
                v.dropOff?.pointOfInterestTypeName,
            },
            {
              label: 'PASSENGER',
              icon: (
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <IconInfo
                    Icon={GroupIcon}
                    title="PASSENGER"
                    value={handleData?.account.mainPassenger.name}
                  />
                  {passengerCount > 1 ? (
                    <Typography
                      component="p"
                      sx={{
                        color: 'var(--jy-navy-900, #00253d)',
                        fontSize: '11px',
                        cursor: 'pointer',
                        position: 'relative',
                        marginLeft: '45px',
                        display: 'flex',
                        flex: 1,
                      }}
                      className="tooltip-wrapper"
                    >
                      {`and ${passengerCount - 1} other ${
                        passengerCount - 1 > 1 ? 'passengers' : 'passenger'
                      }`}
                      <Box component="div" className="tooltip">
                        <Box
                          component="ul"
                          sx={{
                            paddingLeft: '15px',
                          }}
                        >
                          {preview?.passengers
                            ?.filter((v: any) => !v.isLeadPassenger)
                            ?.map((v: any, key: any) => (
                              <Box key={key} component="li">
                                {v.name}
                              </Box>
                            ))}
                        </Box>
                      </Box>
                    </Typography>
                  ) : (
                    <></>
                  )}
                </Box>
              ),
            },
            {
              label: 'VEHICLE(S)',
              value: vehicleCountArray?.map(
                (vehicle: any, keyNumber: number) => (
                  <Typography
                    key={keyNumber}
                    component="p"
                    sx={{
                      color: '#3C4858',
                      fontWeight: 400,
                      fontSize: '12px',
                      lineHeight: '21px',
                    }}
                  >
                    {`${vehicle.count}x ${vehicle.vehicleTypeName}`}
                  </Typography>
                ),
              ),
            },
            {
              label: 'SERVICES',
              value: v.services === 'MeetAndGreet' ? 'Meet and Greet' : null,
            },
          ]
          return (
            <Box
              key={key}
              component="div"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                background: '#fff',
              }}
            >
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  padding: '24px',
                  borderBottom: '1px solid #E8E8E8',
                  justifyContent: 'space-between',
                }}
              >
                <Typography
                  component="p"
                  sx={{
                    color: '#0A2435',
                    fontWeight: 600,
                    fontSize: '19px',
                    lineHeight: '21px',
                    textTransform: 'capitalize',
                  }}
                >
                  {`${v.routeType} Trip`}
                </Typography>
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '24px',
                  }}
                >
                  <Box
                    component="div"
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '8px',
                    }}
                  >
                    <Typography
                      component="p"
                      sx={{
                        color: '#414042',
                        fontWeight: 300,
                        fontSize: '14px',
                        lineHeight: '21px',
                      }}
                    >
                      Pick up time:
                    </Typography>
                    <Typography
                      component="p"
                      sx={{
                        color: '#0A2435',
                        fontWeight: 600,
                        fontSize: '14px',
                        lineHeight: '21px',
                      }}
                    >
                      {v?.isImmediateJourney
                        ? 'ASAP'
                        : moment
                            .utc(v.when)
                            .local()
                            .format('DD MMMM YYYY, h:mm a')}
                    </Typography>
                  </Box>
                  <Box
                    component="div"
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '8px',
                    }}
                  >
                    <Typography
                      component="p"
                      sx={{
                        color: '#414042',
                        fontWeight: 300,
                        fontSize: '14px',
                        lineHeight: '21px',
                      }}
                    >
                      Jyrney Reference:
                    </Typography>
                    <Typography
                      component="p"
                      sx={{
                        color: '#0A2435',
                        fontWeight: 600,
                        fontSize: '14px',
                        lineHeight: '21px',
                      }}
                    >
                      {handleData?.transport?.bookingReference}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  padding: '32px 24px',
                  gap: '50px',
                  justifyContent: 'center',
                }}
              >
                {cardContent?.map((card, keyValue) => {
                  if (card.label === 'SERVICES' && !card.value) return
                  if (
                    card.label === 'VEHICLE(S)' ||
                    card.label === 'SERVICES'
                  ) {
                    return (
                      <Box
                        key={keyValue}
                        component="div"
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          gap: '8px',
                          width: '16.6%',
                        }}
                      >
                        {card.label === 'SERVICES' ? (
                          <Box
                            component="img"
                            src={IconHandshake}
                            alt="info"
                            sx={{
                              width: '22px',
                              height: '22px',
                            }}
                          />
                        ) : (
                          <DirectionsCarIcon />
                        )}
                        <Box
                          component="div"
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            width: 'calc(100% - 30px)',
                          }}
                        >
                          <Typography
                            component="p"
                            sx={{
                              color: 'var(--jy-navy-900, #00253d)',
                              fontWeight: 600,
                              fontSize: '11px',
                              lineHeight: '12.27px',
                            }}
                          >
                            {card.label}
                          </Typography>
                          <Typography
                            component="p"
                            sx={{
                              color: '#3C4858',
                              fontWeight: 400,
                              fontSize: '12px',
                              lineHeight: '21px',
                            }}
                          >
                            {card.value}
                          </Typography>
                        </Box>
                      </Box>
                    )
                  }
                  if (card.label === 'VIA' && preview.additionalStops?.length) {
                    return (
                      <Box
                        key={keyValue}
                        component="div"
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          gap: '8px',
                          width: '16.6%',
                        }}
                      >
                        <LocationOnIcon
                          sx={{
                            color: 'var(--jy-navy-900, #00253d)',
                            width: '32px',
                          }}
                        />
                        <Box
                          component="div"
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            width: 'calc(100% - 30px)',
                          }}
                        >
                          <Typography
                            component="p"
                            sx={{
                              color: 'var(--jy-navy-900, #00253d)',
                              fontWeight: 600,
                              fontSize: '11px',
                              lineHeight: '12.27px',
                            }}
                          >
                            {card.label}
                          </Typography>
                          <Typography
                            component="p"
                            sx={{
                              color: '#3C4858',
                              fontWeight: 400,
                              fontSize: '12px',
                              lineHeight: '21px',
                            }}
                          >
                            {preview.additionalStops[0]?.formattedAddress}
                            {preview.additionalStops?.length > 1 ? (
                              <Typography
                                component="p"
                                sx={{
                                  color: 'var(--jy-navy-900, #00253d)',
                                  fontSize: '11px',
                                  cursor: 'pointer',
                                  position: 'relative',
                                }}
                                className="tooltip-wrapper"
                              >
                                {`and ${
                                  preview.additionalStops?.length - 1
                                } other ${
                                  preview.additionalStops?.length - 1 > 1
                                    ? 'stops'
                                    : 'stop'
                                }`}
                                <Box component="div" className="tooltip">
                                  <Box
                                    component="ul"
                                    sx={{
                                      paddingLeft: '15px',
                                    }}
                                  >
                                    {preview.additionalStops
                                      .filter((_v: any, key: any) => key > 0)
                                      .map((v: any, key: any) => (
                                        <Box key={key} component="li">
                                          {v.formattedAddress}
                                        </Box>
                                      ))}
                                  </Box>
                                </Box>
                              </Typography>
                            ) : (
                              <></>
                            )}
                          </Typography>
                        </Box>
                      </Box>
                    )
                  } else if (card.label !== 'VIA') {
                    return (
                      <Box
                        key={keyValue}
                        component="div"
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          gap: '8px',
                          width: '16.6%',
                        }}
                      >
                        {card.icon || <></>}
                      </Box>
                    )
                  }
                })}
              </Box>
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  padding: '16px 24px',
                  borderTop: '1px solid #E8E8E8',
                }}
              >
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '16px',
                  }}
                >
                  <Button
                    sx={(theme) => ({
                      backgroundColor:
                        theme.components.cancelButton?.backgroundColor ||
                        '#ff0000',
                      color: theme.palette.common.white,
                      '&:hover,&:focus': {
                        color: theme.palette.common.white,
                        backgroundColor: '#ff0000',
                      },
                    })}
                    // color="error"
                    type="button"
                    disabled={handleData?.cancelled === key}
                    onClick={async () => {
                      setCancelTrigger(true)
                      const getTransports = v.transportRef.vehicles

                      const shouldCancelBooking = window.confirm(
                        `Are you sure you want cancel your booking?`,
                      )
                      if (shouldCancelBooking) {
                        await Promise.all(
                          getTransports.map(async (v: any) => {
                            const jobReference = v.jobReference
                            await cancelVehicle(jobReference)
                          }),
                        )
                        addData({ cancelled: key })
                      }
                    }}
                  >
                    {handleData?.cancelled === key
                      ? 'Cancelled'
                      : 'Cancel Booking'}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      clearData()
                      history.push('/admin/bookings/create?new=true')
                    }}
                  >
                    New Booking
                  </Button>
                </Box>
              </Box>
            </Box>
          )
        })}
      </Box>
      {/* Onward journey suggestions - auto-opens as a modal once we have
          quoted suggestions. Dismissable via close / backdrop / ESC. */}
      <Dialog
        open={onwardModalOpen}
        onClose={() => setOnwardModalDismissed(true)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="onward-dialog-title"
      >
        <DialogTitle
          id="onward-dialog-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: 1,
          }}
        >
          <FlightLandIcon
            sx={{ fontSize: 20, verticalAlign: 'text-bottom', mr: 1 }}
          />
          <span>Continue your journey?</span>

          <IconButton
            aria-label="close"
            onClick={() => setOnwardModalDismissed(true)}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          Do you need to take a car to after you land? Let us know and we can
          get a car to wherever you need to go.
        </DialogContent>
        <DialogContent dividers>
          <OnwardBookingCards
            suggestions={onwardBookings.suggestions}
            onSelect={handleOnwardSelect}
          />
        </DialogContent>
      </Dialog>
      <Snackbar open={error} autoHideDuration={3000}>
        <Alert severity="error">
          Booking could not be cancelled as some jobs are still pending
          confirmation.
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default Confirmation
